/**
 * features/applications/EntranceAssessmentPanel.tsx
 * ------------------------------------------------------------------
 * Where the entrance test marks are entered.
 *
 * The school has always run the test and the printed form has always had a
 * box for it — entrance exam date, passed YES / NO — filled in by hand and
 * typed nowhere. So an application could be approved without anything in
 * the record saying whether the child had sat it. The paper knew and the
 * system did not.
 *
 * THE PERCENTAGE IS SHOWN, NOT ACTED ON. No pass mark is encoded anywhere:
 * a school admits on more than an average, and a threshold in code would
 * either be wrong or be ignored. The office reads the number and sets the
 * outcome.
 *
 * A FAIL DOES NOT DISABLE ANYTHING. It is recorded and it is shown, loudly,
 * next to the Approve button. A system that refuses to admit a child the
 * head has decided to admit gets worked around, and the workaround is what
 * stops being recorded.
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import {
  GraduationCap, Plus, Trash2, AlertCircle, CheckCircle2, Save, Loader2,
} from 'lucide-react';
import { doc } from 'firebase/firestore';
import { db, updateDoc } from '@/lib/firebase';
import { auditFromContext } from '@/lib/audit';
import { SoftCard, PanelHeader, glassFieldClass, btnPrimary } from '@/features/finance/ui';
import {
  SUGGESTED_SUBJECTS, OUTCOME_LABEL, markProblem, markPercent, overallPercent,
  assessmentProblem, isOverridingFail,
  type EntranceAssessment, type EntranceMark, type EntranceOutcome,
} from '@/lib/entranceAssessment';

const todayIso = () => new Date().toISOString().slice(0, 10);

const blankMarks = (): EntranceMark[] =>
  SUGGESTED_SUBJECTS.map((s) => ({ subject: s, score: 0, outOf: 100 }));

export default function EntranceAssessmentPanel({
  applicationId,
  applicantName,
  assessment,
  canEdit,
  userData,
}: {
  applicationId: string;
  applicantName: string;
  assessment: EntranceAssessment | null;
  canEdit: boolean;
  userData: { uid?: string; email?: string; displayName?: string; role?: string } | null;
}) {
  const [draft, setDraft] = useState<EntranceAssessment>(
    assessment ?? {
      sittingDate: todayIso(),
      marks: blankMarks(),
      outcome: 'pass',
    },
  );
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const problem = assessmentProblem(draft);
  const pct = overallPercent(draft.marks ?? []);
  const saved = assessmentProblem(assessment) === null;

  function setMark(i: number, patch: Partial<EntranceMark>) {
    setDraft((d) => ({
      ...d,
      marks: (d.marks ?? []).map((m, j) => (j === i ? { ...m, ...patch } : m)),
    }));
    setMsg(null);
  }

  async function save() {
    if (problem) { setMsg({ ok: false, text: problem }); return; }
    setBusy(true);
    try {
      const record: EntranceAssessment = {
        ...draft,
        // An exemption keeps no marks — storing the blank rows it never used
        // would put zeros on a record that says the test was not sat.
        marks: draft.outcome === 'exempt' ? [] : (draft.marks ?? []),
        recordedBy: userData?.uid ?? '',
        recordedByName: userData?.displayName ?? userData?.email ?? '',
        recordedAt: new Date().toISOString(),
      };
      await updateDoc(doc(db, 'reportApplications', applicationId), {
        entranceAssessment: record,
      });
      auditFromContext(userData, 'entrance_assessment_recorded', 'admissions', {
        targetId: applicationId,
        targetType: 'application',
        targetName: applicantName,
        details: { outcome: record.outcome, percent: pct, sittingDate: record.sittingDate },
      });
      setMsg({ ok: true, text: 'Recorded.' });
    } catch (e) {
      setMsg({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally { setBusy(false); }
  }

  return (
    <SoftCard pad="p-0" hover={false}>
      <PanelHeader
        icon={GraduationCap}
        title="Entrance assessment"
        subtitle="Recorded by the Academics Office. Required before approving."
        right={(
          <span className={`text-xs px-2 py-0.5 rounded-full ${saved
            ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'}`}
          >
            {saved ? OUTCOME_LABEL[assessment!.outcome] : 'Not recorded'}
          </span>
        )}
      />

      <div className="p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Date sat
            </label>
            <input
              type="date"
              value={draft.sittingDate}
              disabled={!canEdit}
              onChange={(e) => { setDraft((d) => ({ ...d, sittingDate: e.target.value })); setMsg(null); }}
              className={glassFieldClass("py-2")}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
              Outcome
            </label>
            <select
              value={draft.outcome}
              disabled={!canEdit}
              onChange={(e) => { setDraft((d) => ({ ...d, outcome: e.target.value as EntranceOutcome })); setMsg(null); }}
              className={glassFieldClass("py-2")}
            >
              {(['pass', 'fail', 'exempt'] as EntranceOutcome[]).map((o) => (
                <option key={o} value={o}>{OUTCOME_LABEL[o]}</option>
              ))}
            </select>
          </div>
        </div>

        {draft.outcome !== 'exempt' && (
          <div className="space-y-2">
            {(draft.marks ?? []).map((m, i) => {
              const bad = markProblem(m);
              const one = markPercent(m);
              return (
                <div key={i} className="grid grid-cols-12 gap-2 items-start">
                  <input
                    value={m.subject}
                    disabled={!canEdit}
                    placeholder="Paper"
                    onChange={(e) => setMark(i, { subject: e.target.value })}
                    className={glassFieldClass("py-2 col-span-5")}
                  />
                  <input
                    value={String(m.score)}
                    disabled={!canEdit}
                    inputMode="numeric"
                    onChange={(e) => setMark(i, { score: Number(e.target.value) })}
                    className={glassFieldClass(
                      `py-2 col-span-3 text-right${bad ? ' border-red-300' : ''}`,
                    )}
                  />
                  <span className="col-span-1 text-center text-sm text-gray-400 pt-2">/</span>
                  <input
                    value={String(m.outOf)}
                    disabled={!canEdit}
                    inputMode="numeric"
                    onChange={(e) => setMark(i, { outOf: Number(e.target.value) })}
                    className={glassFieldClass("py-2 col-span-2 text-right")}
                  />
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => setDraft((d) => ({ ...d, marks: d.marks.filter((_, j) => j !== i) }))}
                      className="col-span-1 text-gray-400 hover:text-red-600 pt-2"
                      title="Remove this paper"
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  {one !== null && (
                    <p className="col-span-12 -mt-1 text-xs text-gray-400">{one}%</p>
                  )}
                </div>
              );
            })}

            {canEdit && (
              <button
                type="button"
                onClick={() => setDraft((d) => ({ ...d, marks: [...(d.marks ?? []), { subject: '', score: 0, outOf: 100 }] }))}
                className="text-xs px-2 py-1 rounded-md text-primaryLight hover:bg-gray-100 dark:hover:bg-gray-700 inline-flex items-center gap-1"
              >
                <Plus size={13} /> Add a paper
              </button>
            )}

            {/* SHOWN, NOT ACTED ON. There is no pass mark in the code. */}
            {pct !== null && (
              <p className="text-sm text-gray-700 dark:text-gray-200 pt-1">
                Overall <strong>{pct}%</strong>
                <span className="text-xs text-gray-400 ml-2">
                  weighted by what each paper was out of
                </span>
              </p>
            )}
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
            Notes
            {draft.outcome === 'exempt' && <span className="text-red-500"> *</span>}
            {draft.outcome === 'fail' && (
              <span className="text-gray-400"> — worth saying why, if the school still admits</span>
            )}
          </label>
          <textarea
            value={draft.notes ?? ''}
            disabled={!canEdit}
            rows={2}
            onChange={(e) => { setDraft((d) => ({ ...d, notes: e.target.value })); setMsg(null); }}
            placeholder={draft.outcome === 'exempt'
              ? 'Why this applicant is exempt from the test'
              : 'Anything the head should know'}
            className={glassFieldClass("py-2")}
          />
        </div>

        {problem && canEdit && (
          <p className="text-sm text-amber-700 dark:text-amber-300 flex items-start gap-1.5">
            <AlertCircle size={14} className="mt-0.5 shrink-0" /> {problem}
          </p>
        )}

        {isOverridingFail(assessment) && (
          <p className="text-sm text-red-700 dark:text-red-300 flex items-start gap-1.5 rounded-lg bg-red-50 dark:bg-red-950/30 p-3">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>
              This applicant did not pass the entrance test. Approving is still possible — that
              is the school&rsquo;s decision — but it will be recorded as an approval against the
              result.
            </span>
          </p>
        )}

        {msg && (
          <p className={`text-sm flex items-start gap-1.5 ${msg.ok
            ? 'text-emerald-700 dark:text-emerald-300'
            : 'text-red-700 dark:text-red-300'}`}
          >
            {msg.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0" /> : <AlertCircle size={14} className="mt-0.5 shrink-0" />}
            {msg.text}
          </p>
        )}

        {canEdit && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => { void save(); }}
              disabled={busy || !!problem}
              className={btnPrimary}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {busy ? 'Saving…' : 'Record assessment'}
            </button>
          </div>
        )}

        {assessment?.recordedByName && (
          <p className="text-xs text-gray-400">
            Recorded by {assessment.recordedByName}
            {assessment.recordedAt ? ` on ${assessment.recordedAt.slice(0, 10)}` : ''}.
          </p>
        )}
      </div>
    </SoftCard>
  );
}
